import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { southAfricanProvinces, citiesByProvince } from '../mock';
import { UserPlus, Upload, Mail } from 'lucide-react';
import axios from 'axios';
import { getPasswordError } from '../lib/utils';

const Register = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    alternate_contact: '',
    country: 'South Africa',
    province: '',
    city: '',
    membership_type: '',
    practice_name: '',
    ahpcsa_number: '',
    bhf_number: '',
    student_number: '',
    public_phone: '',
    public_address_line1: '',
    public_address_line2: '',
    termsAccepted: false,
    otp_code: ''
  });
  const [document, setDocument] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { register } = useAuth();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSelectChange = (name, value) => {
    setFormData({
      ...formData,
      [name]: value,
      ...(name === 'province' ? { city: '' } : {}) // Reset city when province changes
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setDocument(file);
    }
  };

  const handleSendOtp = async () => {
    if (!formData.email) {
      toast({ title: 'Enter your email first', variant: 'destructive' });
      return;
    }
    setOtpSending(true);
    try {
      await axios.post(`${backendUrl}/api/auth/request-otp`, {
        email: formData.email,
        purpose: 'registration'
      });
      setOtpSent(true);
      toast({ title: 'OTP sent', description: 'Check your inbox for the 6-digit code (valid for 10 minutes).' });
    } catch (err) {
      const msg = typeof err.response?.data?.detail === 'string' ? err.response.data.detail : 'Failed to send OTP';
      toast({ title: msg, variant: 'destructive' });
    } finally {
      setOtpSending(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    const requiredFields = [
      'email', 'password', 'full_name', 'public_phone', 'public_address_line1',
      'province', 'city', 'membership_type', 'practice_name'
    ];

    // Add profile-specific required fields
    if (formData.membership_type === 'doctor') {
      requiredFields.push('ahpcsa_number', 'bhf_number');
    } else if (formData.membership_type === 'student') {
      requiredFields.push('student_number');
    }

    const missingFields = requiredFields.filter(field => !formData[field]);
    
    if (missingFields.length > 0) {
      toast({
        title: 'Missing fields',
        description: 'Please fill in all required fields.',
        variant: 'destructive'
      });
      return;
    }

    const passwordError = getPasswordError(formData.password);
    if (passwordError) {
      toast({
        title: 'Invalid password',
        description: passwordError,
        variant: 'destructive'
      });
      return;
    }

    if (!formData.termsAccepted) {
      toast({
        title: 'Terms not accepted',
        description: 'You must agree to the Terms & Conditions to register.',
        variant: 'destructive'
      });
      return;
    }

    if (!formData.otp_code || formData.otp_code.length !== 6) {
      toast({
        title: 'OTP required',
        description: 'Please request and enter the 6-digit verification code.',
        variant: 'destructive'
      });
      return;
    }

    if (!document) {
      toast({
        title: 'AHPCSA document required',
        description: 'Please upload your AHPCSA registration document.',
        variant: 'destructive'
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare registration data (exclude termsAccepted and document for now)
      const registrationData = {
        email: formData.email,
        password: formData.password,
        full_name: formData.full_name,
        alternate_contact: formData.alternate_contact,
        country: formData.country,
        province: formData.province,
        city: formData.city,
        membership_type: formData.membership_type,
        practice_name: formData.practice_name,
        ahpcsa_number: formData.ahpcsa_number || null,
        bhf_number: formData.bhf_number || null,
        student_number: formData.student_number || null,
        public_phone: formData.public_phone,
        public_address_line1: formData.public_address_line1,
        public_address_line2: formData.public_address_line2 || null,
        otp_code: formData.otp_code,
      };
      
      const result = await register(registrationData);
      
      if (result.success) {
        // Upload the AHPCSA document now that the user is registered and token is set
        if (document) {
          try {
            const docFormData = new FormData();
            docFormData.append('file', document);
            docFormData.append('document_type', 'AHPCSA');
            docFormData.append('description', 'AHPCSA Registration Document');
            await axios.post(`${backendUrl}/api/user/documents/upload`, docFormData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
            toast({
              title: 'Registration successful!',
              description: 'Your account has been created and your AHPCSA document uploaded. Please wait for admin approval.'
            });
          } catch (docError) {
            toast({
              title: 'Registration successful, but document upload failed',
              description: 'Your account was created but we could not upload your AHPCSA document. Please upload it later from the Documents page.',
              variant: 'destructive'
            });
          }
        } else {
          toast({
            title: 'Registration successful!',
            description: 'Your account has been created. Please wait for admin approval to access member features.'
          });
        }
        
        // Redirect to home after 2 seconds
        setTimeout(() => {
          navigate('/');
        }, 2000);
      } else {
        const description = /otp/i.test(result.error || '')
          ? `${result.error} Request a new code and try again.`
          : (result.error || 'Please try again or contact support.');
        toast({
          title: 'Registration failed',
          description,
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Registration failed',
        description: 'An unexpected error occurred. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white py-12">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Register</h1>
            <p className="text-gray-600">Join Chirosport SA today</p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-2xl">Membership Registration</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Account Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
                    Account Information
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="email">
                        Email Address <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="your.email@example.com"
                        className="mt-2"
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="password">
                        Password <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="password"
                        name="password"
                        type="password"
                        value={formData.password}
                        onChange={handleChange}
                        placeholder="Create a strong password"
                        className="mt-2"
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Min 8 characters with uppercase, lowercase, a number and a special character.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Personal Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
                    Personal Information
                  </h3>

                    <div>
                      <Label htmlFor="full_name">
                        Full Name <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="full_name"
                        name="full_name"
                        value={formData.full_name}
                        onChange={handleChange}
                        placeholder="Dr. John Smith"
                        className="mt-2"
                        required
                      />
                    </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="public_phone">
                        Contact Number (Public) <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="public_phone"
                        name="public_phone"
                        value={formData.public_phone}
                        onChange={handleChange}
                        placeholder="+27 11 234 5678"
                        className="mt-2"
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="alternate_contact">Alternative Number</Label>
                      <Input
                        id="alternate_contact"
                        name="alternate_contact"
                        value={formData.alternate_contact}
                        onChange={handleChange}
                        placeholder="+27 82 345 6789"
                        className="mt-2"
                      />
                    </div>
                  </div>
                </div>

                {/* Address Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
                    Address Information
                  </h3>

                  <div>
                    <Label htmlFor="public_address_line1">
                      Address Line 1 (Public) <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="public_address_line1"
                      name="public_address_line1"
                      value={formData.public_address_line1}
                      onChange={handleChange}
                      placeholder="123 Main Street"
                      className="mt-2"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="public_address_line2">Address Line 2 (Public)</Label>
                    <Input
                      id="public_address_line2"
                      name="public_address_line2"
                      value={formData.public_address_line2}
                      onChange={handleChange}
                      placeholder="Suite 100"
                      className="mt-2"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="country">
                        Country <span className="text-red-500">*</span>
                      </Label>
                      <Select
                        value={formData.country}
                        onValueChange={(value) => handleSelectChange('country', value)}
                      >
                        <SelectTrigger className="mt-2">
                          <SelectValue placeholder="Select country" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="South Africa">South Africa</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="province">
                        Province <span className="text-red-500">*</span>
                      </Label>
                      <Select
                        value={formData.province}
                        onValueChange={(value) => handleSelectChange('province', value)}
                      >
                        <SelectTrigger className="mt-2">
                          <SelectValue placeholder="Select province" />
                        </SelectTrigger>
                        <SelectContent>
                          {southAfricanProvinces.map((province) => (
                            <SelectItem key={province} value={province}>
                              {province}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="city">
                        City <span className="text-red-500">*</span>
                      </Label>
                      <Select
                        value={formData.city}
                        onValueChange={(value) => handleSelectChange('city', value)}
                        disabled={!formData.province}
                      >
                        <SelectTrigger className="mt-2">
                          <SelectValue placeholder="Select city" />
                        </SelectTrigger>
                        <SelectContent>
                          {formData.province &&
                            citiesByProvince[formData.province]?.map((city) => (
                              <SelectItem key={city} value={city}>
                                {city}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Professional Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
                    Professional Information
                  </h3>

                  <div>
                    <Label htmlFor="membership_type">
                      Select Your Profile <span className="text-red-500">*</span>
                    </Label>
                    <Select
                      value={formData.membership_type}
                      onValueChange={(value) => handleSelectChange('membership_type', value)}
                    >
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Select profile type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="doctor">Doctor</SelectItem>
                        <SelectItem value="student">Student</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="practice_name">
                      Practice Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="practice_name"
                      name="practice_name"
                      value={formData.practice_name}
                      onChange={handleChange}
                      placeholder="Smith Chiropractic Clinic"
                      className="mt-2"
                      required
                    />
                  </div>

                  {formData.membership_type === 'doctor' && (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="ahpcsa_number">
                            AHPCSA Number <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            id="ahpcsa_number"
                            name="ahpcsa_number"
                            value={formData.ahpcsa_number}
                            onChange={handleChange}
                            placeholder="AHPCSA registration number"
                            className="mt-2"
                            required
                          />
                        </div>

                        <div>
                          <Label htmlFor="bhf_number">
                            BHF Number <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            id="bhf_number"
                            name="bhf_number"
                            value={formData.bhf_number}
                            onChange={handleChange}
                            placeholder="Board of Healthcare Funders number"
                            className="mt-2"
                            required
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {formData.membership_type === 'student' && (
                    <div>
                      <Label htmlFor="student_number">
                        Chirosport Student Number <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="student_number"
                        name="student_number"
                        value={formData.student_number}
                        onChange={handleChange}
                        placeholder="Your student number"
                        className="mt-2"
                        required
                      />
                    </div>
                  )}

                  <div>
                    <Label htmlFor="document">
                      Upload AHPCSA Document <span className="text-red-500">*</span>
                    </Label>
                    <div className="mt-2">
                      <label
                        htmlFor="document"
                        className="flex items-center justify-center w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 transition-colors"
                      >
                        <Upload size={20} className="mr-2 text-gray-500" />
                        <span className="text-sm text-gray-600">
                          {document ? document.name : 'Choose file or drag here'}
                        </span>
                      </label>
                      <input
                        id="document"
                        type="file"
                        onChange={handleFileChange}
                        accept=".pdf,.jpg,.jpeg,.png"
                        className="hidden"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Accepted formats: PDF, JPG, PNG (Max 5MB)
                    </p>
                  </div>
                </div>

                {/* OTP Email Verification */}
                <div className="space-y-3 p-4 bg-blue-50 border border-blue-200 rounded-lg" data-testid="otp-section">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <Mail size={20} className="text-blue-600" /> Email Verification
                  </h3>
                  <p className="text-sm text-gray-600">
                    We&rsquo;ll email a 6-digit code to <strong>{formData.email || 'your address'}</strong> to verify it.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Input
                      name="otp_code"
                      value={formData.otp_code}
                      onChange={handleChange}
                      placeholder="6-digit code"
                      maxLength={6}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      className="flex-1"
                      data-testid="otp-code-input"
                    />
                    <Button
                      type="button"
                      onClick={handleSendOtp}
                      disabled={otpSending || !formData.email}
                      variant="outline"
                      data-testid="send-otp-btn"
                    >
                      {otpSending ? 'Sending&hellip;' : otpSent ? 'Resend OTP' : 'Send OTP'}
                    </Button>
                  </div>
                  {otpSent && (
                    <p className="text-xs text-green-700">
                      Code sent. Check your inbox (and spam folder). Code expires in 10 minutes.
                    </p>
                  )}
                </div>

                {/* Terms and Conditions */}
                <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg">
                  <Checkbox
                    id="termsAccepted"
                    name="termsAccepted"
                    checked={formData.termsAccepted}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, termsAccepted: checked })
                    }
                  />
                  <Label htmlFor="termsAccepted" className="text-sm cursor-pointer leading-relaxed">
                    I agree to the{' '}
                    <Link to="/terms" className="text-blue-600 hover:text-blue-700" target="_blank">
                      Terms & Conditions
                    </Link>{' '}
                    <span className="text-red-500">*</span>
                  </Label>
                </div>

                <div className="text-sm text-gray-500">
                  <span className="text-red-500">*</span> are required fields
                </div>

                {/* Submit Buttons */}
                <div className="flex gap-3 pt-4">
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                  >
                    {isSubmitting ? (
                      'Registering...'
                    ) : (
                      <>
                        <UserPlus size={18} className="mr-2" />
                        Register
                      </>
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/login')}
                  >
                    Back to Login
                  </Button>
                </div>

                <div className="text-center text-sm text-gray-600">
                  Already have an account?{' '}
                  <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium">
                    Login here
                  </Link>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Register;
